import { t, LanguageSelector } from '../i18n';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Chip,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  CloudSync as CloudIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';

interface UserInfo {
  username: string;
  displayName: string;
  role: string;
  orgName: string;
  orgIcon: string;
  loginTime: string;
}

interface KAISTHeaderProps {
  user: UserInfo;
  onMenuClick: () => void;
  onLogout?: () => void;
}

const KAISTHeader: React.FC<KAISTHeaderProps> = ({ user, onMenuClick, onLogout }) => {
  const navigate = useNavigate();
  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
      }}
    >
      <Toolbar sx={{ minHeight: '70px !important', px: 3 }}>
        <IconButton color="inherit" onClick={onMenuClick} edge="start" sx={{ marginRight: 2 }}>
          <MenuIcon />
        </IconButton>
        
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <CloudIcon sx={{ mr: 1.5, fontSize: 28, color: '#E3F2FD' }} />
          <Box>
            <Typography variant="h6" noWrap component="div" sx={{
              fontWeight: 'bold', letterSpacing: '0.5px', lineHeight: 1.2
            }}>{t("High-Reliability Intelligent Smart Port Logistics")}</Typography>
            <Typography variant="caption" sx={{
              color: '#E3F2FD', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.3px'
            }}>{t("Blockchain Management System")}</Typography>
          </Box>
        </Box>        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: '0.3px',
              display: { xs: 'none', lg: 'block' }
            }}
          >{t("Projected by SOCFAI")}</Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              backgroundColor: 'rgba(255,255,255,0.3)',
              height: 40,
              display: { xs: 'none', lg: 'block' }
            }}
          />
          <Chip
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            label={t("KAIST Web Portal")}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.8rem',
              height: 32,
              display: { xs: 'none', md: 'flex' },
              '& .MuiChip-icon': {
                color: 'white'
              },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.25)'
              }
            }}
          />

          {user && (
            <Typography variant="body2" sx={{ color: '#E3F2FD', mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {user.displayName?.replace('SOCFAI ', '') || t("KAIST User")}
            </Typography>
          )}

          <LanguageSelector />
          {onLogout && (
            <IconButton
              onClick={onLogout}
              sx={{
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                px: { xs: 1, sm: 2 },
                height: 32,
                '&:hover': { borderColor: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' }, color: 'white', fontSize: '0.8rem' }}>{t("Logout")}</Typography>
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default KAISTHeader;